import { IDataTableColumn } from '@srm/ui/data-table';
import DisplayField from '@molecules/DisplayField/DisplayField';
import { IDictionaryData } from '@models/dictionary/IDictionaryData';
import { IGridModalAttribute, IGridModalAttributes } from '@models/Forms/IForms';
import { ValueType } from '@srm/ui/types/ValueType';

/** Тип атрибута справочника (нотация схемы) → тип колонки таблицы. */
export const dictValueType = (type: IGridModalAttribute['type']): ValueType => {
    switch (type) {
        case 'number':
            return ValueType.Double;
        case 'boolean':
            return ValueType.Boolean;
        case 'date':
            return ValueType.Date;
        case 'datetime':
            return ValueType.DateTime;
        default:
            return ValueType.Text;
    }
};

/** Значение атрибута справочника по позиции в схеме (`fields[i].value`). */
export const dictFieldValue = (row: IDictionaryData, index: number): unknown => row.fields?.[index]?.value;

/**
 * Колонки окна справочника по шаблону: без колонок только для строки выпадающего списка (`dropdownOnly`) —
 * разметка списка окно не меняет. Шаблон без колонок окна — как его отсутствие: окно показывает колонки схемы,
 * а не пустую таблицу (так бывает у полей, которые раньше были только выпадающим списком).
 */
export const modalGridAttribute = (gridAttribute?: IGridModalAttributes): IGridModalAttributes | undefined => {
    if (!gridAttribute || gridAttribute.dropdownOnly) return undefined;
    const attrs = gridAttribute.attrs.filter((attr) => !attr.dropdownOnly);
    if (!attrs.length) return undefined;
    return attrs.length === gridAttribute.attrs.length ? gridAttribute : { ...gridAttribute, attrs };
};

/** Поле колонки атрибута: адресация, которую понимает бэкенд в loadOptions. */
export const dictFieldName = (index: number) => `fields[${index}].value`;

/** Поле loadOptions по ключу колонки шаблона: `code` или атрибут схемы; неизвестный схеме ключ — нет поля. */
export const dictAttrField = (key: string, dictAttributes: string[]): string | undefined => {
    if (key === 'code') return 'code';
    const index = dictAttributes.indexOf(key);
    return index >= 0 ? dictFieldName(index) : undefined;
};

/** Значение колонки присоединённого справочника: бэкенд кладёт его в строку под `joined[коллекция][имя]`. */
export const dictJoinedValue = (row: IDictionaryData, attr: IGridModalAttribute): unknown =>
    (row as unknown as Record<string, Record<string, Record<string, unknown>>>).joined?.[attr.collection]?.[attr.name];

/**
 * Ссылка вместо значения: у псевдосправочников (данные документа) строка может
 * нести `url`, и колонки из `urlAttributes` показывают значение ссылкой.
 */
const urlCell = (row: IDictionaryData, value: unknown, header: string, isFormData: boolean) =>
    isFormData && row.url && row.urlAttributes?.indexOf(header) !== -1 ? (
        <a target="_blank" href={row.url} rel="noopener,noreferrer">
            {String(value ?? '')}
        </a>
    ) : (
        <>{String(value ?? '')}</>
    );

export interface IDictColumnsOptions {
    /** Атрибуты справочника в порядке схемы (индекс = позиция поля в строке). */
    dictAttributes: string[];
    /** Колонки из шаблона: имена, ширины, типы и порядок показа. */
    gridAttribute?: IGridModalAttributes;
    /** Псевдосправочник данных документа: у строк бывают ссылки. */
    isFormData: boolean;
}

/**
 * Колонки списка справочника: код плюс атрибуты схемы.
 *
 * Порядок и вид колонок задаёт шаблон (`<gridAttributes>`): перечисленные в нём
 * идут первыми в его порядке, остальные — как пришли со схемой. Без шаблона
 * показываем всё подряд текстом, как это делал DX-грид.
 *
 * Логические значения справочник отдаёт строкой «0»/«1», поэтому рисуем их
 * общим `DisplayField`, а не сравнением с truthy (иначе «0» выглядит отмеченным).
 */
export function buildDictColumns({
    dictAttributes,
    gridAttribute,
    isFormData,
}: IDictColumnsOptions): IDataTableColumn<IDictionaryData>[] {
    const attrByKey = new Map((gridAttribute?.attrs ?? []).map((attr) => [attr.key, attr]));
    const templateOrder = (gridAttribute?.attrs ?? []).map((attr) => attr.key);
    const orderOf = (key: string) => {
        const index = templateOrder.indexOf(key);
        return index === -1 ? Number.POSITIVE_INFINITY : index;
    };

    const columns: IDataTableColumn<IDictionaryData>[] = [];

    const codeAttr = attrByKey.get('code');
    if (codeAttr) {
        columns.push({
            field: 'code',
            header: codeAttr.name,
            dataType: dictValueType(codeAttr.type),
            width: codeAttr.width || undefined,
            headerNoEllipsis: codeAttr.headerNoEllipsis,
        });
    } else if (gridAttribute === undefined) {
        columns.push({ field: 'code', header: 'Код', dataType: ValueType.Text, widthHint: true, width: 160 });
    }

    [...dictAttributes]
        .map((key, index) => ({ key, index }))
        .sort((a, b) => orderOf(a.key) - orderOf(b.key))
        .forEach(({ key, index }) => {
            const attr = attrByKey.get(key);
            // шаблон перечислил колонки — значит показываем только их
            if (!attr && gridAttribute !== undefined) return;
            const header = attr?.name ?? key;
            const dataType = dictValueType(attr?.type);
            columns.push({
                field: dictFieldName(index),
                header,
                dataType,
                accessor: (row) => dictFieldValue(row, index),
                cellText: (row) => String(dictFieldValue(row, index) ?? ''),
                width: attr?.width || undefined,
                widthHint: !attr?.width,
                headerNoEllipsis: attr?.headerNoEllipsis,
                render:
                    dataType === ValueType.Boolean
                        ? (row) => <DisplayField type={ValueType.Boolean} value={dictFieldValue(row, index)} />
                        : attr?.type
                          ? undefined
                          : (row) => urlCell(row, dictFieldValue(row, index), header, isFormData),
            });
        });

    return columns;
}

/**
 * Колонки присоединённых справочников (`joined` у атрибута шаблона): значение
 * лежит в строке под путём `joined[коллекция][имя]`, который так и приходит с
 * бэкенда, поэтому читаем его сами, а не полем.
 */
export function buildDictJoinedColumns(gridAttribute?: IGridModalAttributes): IDataTableColumn<IDictionaryData>[] {
    return (gridAttribute?.attrs ?? [])
        .filter((attr) => attr.joined)
        .map((attr) => {
            const value = (row: IDictionaryData) => dictJoinedValue(row, attr);
            return {
                field: `joined[${attr.collection}][${attr.name}]`,
                header: attr.name,
                dataType: dictValueType(attr.type),
                accessor: value,
                cellText: (row: IDictionaryData) => String(value(row) ?? ''),
                width: attr.width || undefined,
                widthHint: !attr.width,
                headerNoEllipsis: attr.headerNoEllipsis,
            };
        });
}

/** Стартовая сортировка из шаблона колонок (`sortOrder` у атрибута). */
export function buildDictSort({ dictAttributes, gridAttribute }: Omit<IDictColumnsOptions, 'isFormData'>) {
    // колонку, которой нет в схеме, не сортируем: fields[-1] сервер не поймёт
    return (gridAttribute?.attrs ?? []).flatMap((attr) => {
        const field = attr.sortOrder ? dictAttrField(attr.key, dictAttributes) : undefined;
        return field ? [{ field, dir: attr.sortOrder === 'desc' ? ('desc' as const) : ('asc' as const) }] : [];
    });
}
